import { createBrowserRouter, Navigate, useParams, type RouteObject } from 'react-router-dom';
import { UserRole } from '@socialsoup/shared';
import { AuthGuard } from '@/features/auth/components/AuthGuard';
import { RoleGuard } from '@/features/auth/components/RoleGuard';
import { PortalLayout } from '@/components/layout/PortalLayout';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { RegisterPage } from '@/features/auth/pages/RegisterPage';
import { useAuthStore } from '@/stores/authStore';
import { ROLE_TO_PORTAL } from '@/lib/constants';
import { LegacyCampaignRedirect } from '@/features/admin/pages/LegacyCampaignRedirect';

// R4.R3 — Alias redirect for /admin/groups/:id → /admin/campaigns/:id
function GroupAliasRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/admin/campaigns/${id}`} replace />;
}

// rapidpull-post-sync Phase 4 — the AI report lives on the channel page's `#ai-report` tab.
function ChannelAiReportRedirect() {
  const { groupId, channelId } = useParams<{ groupId: string; channelId: string }>();
  return <Navigate to={`/admin/campaigns/${groupId}/channels/${channelId}#ai-report`} replace />;
}

/**
 * Component that redirects the root path to the correct portal
 * based on the authenticated user's role.
 */
function RoleRedirect() {
  const { user, isAuthenticated } = useAuthStore();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const portalPath = ROLE_TO_PORTAL[user.role] ?? '/login';
  return <Navigate to={portalPath} replace />;
}

// Phase 8a task 9 (design decision 9) — exported so a unit test can
// `matchRoutes` against the real route table without rendering anything
// (no auth-store shape, no lazy imports to resolve).
export const routes: RouteObject[] = [
  // Public routes
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  { path: '/auth/forgot-password', lazy: () => import('@/features/auth/pages/ForgotPasswordPage') },
  { path: '/reset-password', lazy: () => import('@/features/auth/pages/ResetPasswordPage') },
  // Phase 5 (TODO-114): auto-login landing — PUBLIC (reachable unauthenticated).
  { path: '/auth/magic', lazy: () => import('@/features/auth/pages/MagicLoginPage') },
  // sso-login Phase 4: SSO handoff landing — PUBLIC (reachable unauthenticated).
  { path: '/auth/sso/complete', lazy: () => import('@/features/auth/pages/SsoCompletePage') },

  // Root redirect
  { path: '/', element: <RoleRedirect /> },

  // Admin Portal
  {
    path: '/admin',
    element: (
      <AuthGuard>
        <RoleGuard allowed={[UserRole.ADMIN]}>
          <PortalLayout role="admin" />
        </RoleGuard>
      </AuthGuard>
    ),
    children: [
      { index: true, lazy: () => import('@/features/admin/pages/DashboardPage') },
      { path: 'review/:id', lazy: () => import('@/features/admin/pages/ReviewDetailPage') },

      // ─── Campaign domain (R4) ────────────────────────────────────────────────
      // New canonical routes.
      // ORDERING MATTERS: more-specific (more-segment) routes MUST be declared
      // before less-specific ones so React Router matches them first.
      // 'campaigns/new' must precede 'campaigns/:groupId' so the literal "new"
      // does not get consumed as a groupId param.
      { path: 'campaigns', lazy: () => import('@/features/admin/pages/CampaignsPage') },
      // "new" redirects to the list; creation happens via the "New Campaign" button there
      { path: 'campaigns/new', element: <Navigate to="/admin/campaigns" replace /> },
      {
        path: 'campaigns/:groupId',
        lazy: () => import('@/features/admin/pages/CampaignGroupDetailPage'),
      },
      {
        path: 'campaigns/:groupId/channels/:channelId',
        lazy: () => import('@/features/admin/pages/ChannelDashboardPage'),
      },
      {
        path: 'campaigns/:groupId/channels/:channelId/posts',
        lazy: () => import('@/features/admin/pages/CampaignPostsTab'),
      },
      {
        path: 'campaigns/:groupId/channels/:channelId/ai-report',
        element: <ChannelAiReportRedirect />,
      },
      {
        path: 'campaigns/:groupId/channels/:channelId/shortlist',
        lazy: () => import('@/features/admin/pages/CreatorShortlistPage'),
      },
      {
        path: 'campaigns/:groupId/channels/:channelId/magic-links',
        lazy: () => import('@/features/admin/pages/MagicLinkManagementPage'),
      },

      // Legacy redirects (R4.R2 — keep working forever).
      // Sub-path redirects must appear before the bare :legacyId catch-all.
      { path: 'campaigns/:legacyId/posts', element: <LegacyCampaignRedirect suffix="/posts" /> },
      {
        path: 'campaigns/:legacyId/ai-report',
        element: <LegacyCampaignRedirect suffix="#ai-report" />,
      },
      {
        path: 'campaigns/:legacyId/shortlist',
        element: <LegacyCampaignRedirect suffix="/shortlist" />,
      },
      {
        path: 'campaigns/:legacyId/magic-links',
        element: <LegacyCampaignRedirect suffix="/magic-links" />,
      },
      { path: 'campaigns/:legacyId', element: <LegacyCampaignRedirect suffix="" /> },

      // R2 groups alias redirects (R4.R3)
      { path: 'groups', element: <Navigate to="/admin/campaigns" replace /> },
      { path: 'groups/:id', element: <GroupAliasRedirect /> },
      // ────────────────────────────────────────────────────────────────────────

      { path: 'pipelines', lazy: () => import('@/features/admin/pages/PipelinesPage') },
      { path: 'pipelines/:id', lazy: () => import('@/features/admin/pages/PipelinesPage') },

      // Consumer Intelligence reports (Phase 3 preview; Phase 13 request/
      // status/report flow). Literal segments ("intel-reports", "new",
      // "preview") declared ahead of the `intel-reports/:id` detail route so
      // none of them can ever be shadowed (same ordering convention as
      // campaigns above).
      { path: 'intel-reports', lazy: () => import('@/features/admin/pages/IntelReportsPage') },
      {
        path: 'intel-reports/new',
        lazy: () => import('@/features/admin/pages/IntelReportNewPage'),
      },
      {
        path: 'intel-reports/preview',
        lazy: () => import('@/features/admin/pages/IntelReportPreviewPage'),
      },
      {
        path: 'intel-reports/:id',
        lazy: () => import('@/features/admin/pages/IntelReportDetailPage'),
      },
      {
        path: 'clienthub',
        lazy: () => import('@/features/admin/pages/ClientHubSyncPage'),
      },
      {
        path: 'clienthub/dashboards',
        lazy: () => import('@/features/admin/pages/ClientDashboardsPage'),
      },
      { path: 'influencers', lazy: () => import('@/features/admin/pages/InfluencersPage') },
      { path: 'influencers/:id', lazy: () => import('@/features/admin/pages/CreatorDetailPage') },
      {
        path: 'creator-home',
        lazy: () => import('@/features/admin/pages/PartnershipMembersPage'),
      },
      {
        path: 'creator-home/members/:membershipId',
        lazy: () => import('@/features/admin/pages/PartnershipMemberDetailPage'),
      },
      {
        path: 'creators',
        lazy: () => import('@/features/admin/pages/CreatorIntelligencePage'),
      },
      {
        path: 'creators/runs/:runId',
        lazy: () => import('@/features/admin/pages/DiscoveryRunDetailPage'),
      },
      { path: 'clients', lazy: () => import('@/features/admin/pages/ClientsPage') },
      { path: 'clients/:id', lazy: () => import('@/features/admin/pages/ClientDetailPage') },
      { path: 'users', lazy: () => import('@/features/admin/pages/UsersPage') },
      { path: 'user', element: <Navigate to="/admin/users" replace /> },
      { path: 'analytics', lazy: () => import('@/features/admin/pages/AnalyticsPage') },
      {
        path: 'analytics/survey-analysis',
        lazy: () => import('@/features/admin/pages/SurveyAnalysisPage'),
      },
      { path: 'notifications', lazy: () => import('@/features/admin/pages/NotificationsPage') },
      { path: 'settings', lazy: () => import('@/features/admin/pages/SettingsPage') },
      // theme-customization A3 — the appearance editor. Nested under this ADMIN guard (only an
      // ADMIN may PUT the palette), and lazy like every other admin page. The literal path
      // `/admin/settings/appearance` is the one the Settings card links to and the one the
      // browser evidence navigates to; the LITERAL segment is declared after `settings` and
      // shadows nothing, since `settings` has no `:param` child.
      {
        path: 'settings/appearance',
        lazy: () => import('@/features/admin/pages/AppearancePage'),
      },
      {
        path: 'email-templates',
        lazy: () => import('@/features/admin/pages/EmailTemplatesPage'),
      },
      {
        path: 'email-log',
        lazy: () => import('@/features/admin/pages/EmailLogPage'),
      },
      // team-training-guide Phase 2 — the in-app Help shell. Admin only in this
      // phase; the pages live under `_shared` because later phases mount the same
      // pages under the client and influencer portals.
      { path: 'help', lazy: () => import('@/features/_shared/help/HelpIndexPage') },
      {
        path: 'help/journeys/:journey',
        lazy: () => import('@/features/_shared/help/JourneyGuidePage'),
      },
    ],
  },

  // Influencer Portal
  {
    path: '/influencer',
    element: (
      <AuthGuard>
        <RoleGuard allowed={[UserRole.INFLUENCER]}>
          <PortalLayout role="influencer" />
        </RoleGuard>
      </AuthGuard>
    ),
    children: [
      // Phase 8a task 9 (design decision 9, owner decision 2) — Campaigns is
      // the post-login landing page; Dashboard is no longer in the nav but
      // stays reachable by direct URL at `dashboard` (same "hidden from nav,
      // route stays registered" precedent as the client portal's Performance
      // page above).
      { index: true, element: <Navigate to="campaigns" replace /> },
      { path: 'dashboard', lazy: () => import('@/features/influencer/pages/DashboardPage') },
      { path: 'campaigns', lazy: () => import('@/features/influencer/pages/CampaignsPage') },
      {
        path: 'campaigns/:id',
        lazy: () => import('@/features/influencer/pages/CampaignDetailPage'),
      },
      // concept-approval-flow Phase 3 — literal `concepts` + `concepts/:id`
      // children. No `:submissionId`-style catch-all exists under
      // `/influencer` today, but declaring the specific-first still holds.
      { path: 'concepts', lazy: () => import('@/features/influencer/pages/ConceptsPage') },
      { path: 'concepts/:id', lazy: () => import('@/features/influencer/pages/ConceptsPage') },
      { path: 'submissions', lazy: () => import('@/features/influencer/pages/SubmissionsPage') },
      {
        path: 'submissions/:id',
        lazy: () => import('@/features/influencer/pages/SubmissionDetailPage'),
      },
      { path: 'upload', lazy: () => import('@/features/influencer/pages/UploadPage') },
      { path: 'upload/:campaignId', lazy: () => import('@/features/influencer/pages/UploadPage') },
      {
        path: 'notifications',
        lazy: () => import('@/features/influencer/pages/NotificationsPage'),
      },
      { path: 'profile', lazy: () => import('@/features/influencer/pages/ProfilePage') },
      { path: 'settings', lazy: () => import('@/features/influencer/pages/SettingsPage') },
      // team-training-guide Phase 9 — the creator Help area: the same `_shared` pages the
      // admin and client branches mount; they read the viewer's role from the URL, so a
      // creator sees only creator steps.
      { path: 'help', lazy: () => import('@/features/_shared/help/HelpIndexPage') },
      {
        path: 'help/journeys/:journey',
        lazy: () => import('@/features/_shared/help/JourneyGuidePage'),
      },
    ],
  },

  // Client Portal
  {
    path: '/client',
    element: (
      <AuthGuard>
        <RoleGuard allowed={[UserRole.CLIENT]}>
          <PortalLayout role="client" />
        </RoleGuard>
      </AuthGuard>
    ),
    children: [
      { index: true, lazy: () => import('@/features/client/pages/DashboardPage') },
      { path: 'review/:id', lazy: () => import('@/features/client/pages/ReviewDetailPage') },
      { path: 'campaigns', lazy: () => import('@/features/client/pages/CampaignsPage') },
      // concept-approval-flow Phase 5 — the client portal's Concepts queue.
      { path: 'concepts', lazy: () => import('@/features/client/pages/ConceptsPage') },
      { path: 'approved', lazy: () => import('@/features/client/pages/ApprovedContentPage') },
      // Decision D-12 (audit 2026-10-05, A-61): the Performance page is retired.
      // The path stays registered so a bookmark lands on the dashboard, which
      // shows a one-time notice, instead of falling through to the catch-all.
      {
        path: 'performance',
        element: <Navigate to="/client" replace state={{ notice: 'performance-retired' }} />,
      },
      {
        path: 'campaigns/:id/reviews',
        lazy: () => import('@/features/client/pages/CampaignReviewsPage'),
      },
      {
        path: 'campaigns/:id/creators',
        lazy: () => import('@/features/client/pages/CreatorApprovalPage'),
      },
      {
        path: 'campaigns/:id/creators/:shortlistId',
        lazy: () => import('@/features/client/pages/CreatorProfilePage'),
      },
      // PR-C (roadmap 1.4/1.6): brief approval + campaign results
      {
        path: 'campaigns/:id/brief',
        lazy: () => import('@/features/client/pages/ClientBriefPage'),
      },
      {
        path: 'campaigns/:id/results',
        lazy: () => import('@/features/client/pages/ClientDebriefPage'),
      },
      { path: 'guidelines', lazy: () => import('@/features/client/pages/GuidelinesPage') },
      { path: 'notifications', lazy: () => import('@/features/client/pages/NotificationsPage') },
      // team-training-guide Phase 7 — the client Help area: the same `_shared` pages
      // the admin branch mounts; they read the viewer's role from the URL, so a client
      // sees only client steps.
      { path: 'help', lazy: () => import('@/features/_shared/help/HelpIndexPage') },
      {
        path: 'help/journeys/:journey',
        lazy: () => import('@/features/_shared/help/JourneyGuidePage'),
      },
    ],
  },

  // Agent Portal
  {
    path: '/agent',
    element: (
      <AuthGuard>
        <RoleGuard allowed={[UserRole.AGENT]}>
          <PortalLayout role="agent" />
        </RoleGuard>
      </AuthGuard>
    ),
    children: [
      { index: true, lazy: () => import('@/features/agent/pages/DashboardPage') },
      { path: 'talent', lazy: () => import('@/features/agent/pages/TalentRosterPage') },
      { path: 'talent/:id', lazy: () => import('@/features/agent/pages/TalentDetailPage') },
      { path: 'campaigns', lazy: () => import('@/features/agent/pages/CampaignsPage') },
      { path: 'submissions', lazy: () => import('@/features/agent/pages/SubmissionsPage') },
      { path: 'reports', lazy: () => import('@/features/agent/pages/ReportsPage') },
      { path: 'notifications', lazy: () => import('@/features/agent/pages/NotificationsPage') },
    ],
  },

  // Inbound Creator Application (public — no AuthGuard, Phase 6)
  { path: '/apply/:token', lazy: () => import('@/features/creator-application/ApplyPage') },

  // Consumer Intelligence public microsite (public — no AuthGuard, Phase 12)
  { path: '/r/:slug', lazy: () => import('@/features/intel-report/pages/PublicReportPage') },

  // Public "How to review" guide (public — no AuthGuard; team-training-guide Phase 8)
  { path: '/how-to-review', lazy: () => import('@/features/help-public/HowToReviewPage') },

  // Magic Link Review (public — no AuthGuard)
  {
    path: '/review/:token',
    lazy: () => import('@/features/creator-review/components/BrandedLayout'),
    children: [
      { index: true, lazy: () => import('@/features/creator-review/pages/MagicLinkLanding') },
      {
        path: 'identify',
        lazy: () => import('@/features/creator-review/pages/ClientIdentificationPage'),
      },
      {
        path: 'creators',
        lazy: () => import('@/features/creator-review/pages/CreatorShortlistView'),
      },
      {
        path: 'creators/:shortlistId',
        lazy: () => import('@/features/creator-review/pages/CreatorProfileView'),
      },
    ],
  },

  // Content Approval Magic Link (public — no AuthGuard). R2: a SEPARATE link type
  // from /review/:token above; a creator token 401s here and vice versa.
  // Deep link: /content-review/:token#<submissionId> lands on that item.
  {
    path: '/content-review/:token',
    lazy: () => import('@/features/content-review/pages/ContentReviewLayout'),
    children: [
      {
        index: true,
        lazy: () => import('@/features/content-review/pages/ContentReviewLanding'),
      },
      {
        path: 'queue',
        lazy: () => import('@/features/content-review/pages/ContentQueueView'),
      },
      // concept-approval-flow Phase 5 — literal `concepts` + `concepts/:conceptId`
      // children, declared BEFORE the bare `:submissionId` catch-all below (it
      // would otherwise swallow both as a submission id).
      {
        path: 'concepts',
        lazy: () => import('@/features/content-review/pages/ConceptQueueView'),
      },
      {
        path: 'concepts/:conceptId',
        lazy: () => import('@/features/content-review/pages/ConceptDetailView'),
      },
      {
        path: ':submissionId',
        lazy: () => import('@/features/content-review/pages/ContentDetailView'),
      },
    ],
  },

  // 404 — catch-all
  {
    path: '*',
    lazy: () => import('@/pages/NotFoundPage'),
  },
];

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(routes);
